import { Link } from '@tanstack/react-router';
import {
  regionLabel,
  deadlineLabel,
  grantPhase,
  supportHeadline,
  GRANT_SUPPORT_FILTERS,
  SUPPORT_VIEW_LABEL,
  type GrantListItemDto,
  type GrantPhaseKey,
  type GrantSupportFilter,
} from '@zoxo/shared';

import { AttachmentPill, pastTitleClass } from './grant-parts';
import { cn } from '../../lib/utils';
import { shortSourceName } from './source-label';

/**
 * 공고 한 건.
 *
 * **첫 열이 무엇을 주는지 말한다.** 기존 목록 UI(기업마당 등)는 그 자리에
 * 아무 의미 없는 일련번호를 둔다. 우리는 해석기가 원문에서 뽑아 검증한
 * 지원 내용을 둔다 — 「최대 5,000만원」「연 2.5% · 최대 5억원」「멘토링 · 교육」.
 */

/**
 * 진행 단계별 색. 🟢 모집중 · 🟡 곧 마감 · 🔴 마감 · 🔵 선정 결과 · ⚫ 종료.
 *
 * 마감일 있음/없음을 구별하는 것만으로는 부족했다 — 결과 공고는 마감일이
 * 지났어도 「마감」이 아니고, 폐지된 사업은 마감과 다른 사실이다.
 */
const PHASE_CLASS: Record<GrantPhaseKey, string> = {
  open: 'bg-[#18BB69] text-white',
  closing: 'bg-[#FACC15] font-bold text-[#111827]',
  closed: 'bg-gray-100 text-gray-500',
  result: 'bg-sky-50 text-sky-700',
  ended: 'bg-gray-100 text-gray-400',
};

/**
 * 목록 첫 열 — **무엇을 얼마나 주나.**
 *
 * 예전엔 여기에 「2,439자」가 있었다. 그건 우리 배치가 공고문에서 몇 글자를
 * 뽑았는지이지 **독자가 알고 싶은 것이 아니다.** 해석 계층이 생기기 전에는
 * 그게 우리가 아는 유일한 사실이라 그 자리를 채웠던 것인데, 이제는 아니다.
 *
 * **유형마다 다른 질문에 답한다** — 보조금·바우처는 「기업당 얼마까지」,
 * 융자는 「한도와 금리」, 보증은 「조건」, 프로그램은 「무슨 종류」다. 한
 * 공고가 여러 유형을 가지면 여러 줄이 된다(창업패키지 = 사업화자금 + 멘토링).
 *
 * 아직 해석 안 한 공고는 **아무 말도 하지 않는다.** 「금액 미정」 같은 빈
 * 말을 채우면 값이 있는 카드와 없는 카드가 같은 무게로 보인다.
 *
 * **유형 필터가 켜져 있으면 그 유형만 보여준다.** 「프로그램」을 고른 사람에게
 * 보조금·바우처·융자를 세 줄 더 얹는 것은 이미 답한 질문을 다시 묻는 것이다.
 * 필터가 곧 의도라, 나머지는 그 순간 소음이다.
 */
const SUPPORT_TONE: Record<GrantSupportFilter, string> = {
  grant: 'text-[#DF301C]',
  loan: 'text-[#4E1F6E]',
  facility: 'text-[#FF9100]',
  program: 'text-[#1B2CC1]',
};

const SUPPORT_ORDER = GRANT_SUPPORT_FILTERS;

function Support({
  grant,
  only,
}: {
  grant: GrantListItemDto;
  only?: GrantSupportFilter;
}) {
  /*
    유형 탭(only)에서는 **그 유형 줄만** 보인다 — 보조금을 눌렀으면 보조금이 관심사다.
    전체 탭만 자금·바우처·융자·시설·프로그램 순으로 전부 보인다.
  */
  const keys: readonly GrantSupportFilter[] = only ? [only] : SUPPORT_ORDER;
  const lines = keys.flatMap((k) => {
    const s = grant.support[k];
    if (!s) return [];
    const text = supportHeadline(k, s);
    if (!text) return [];
    // 여러 유형이 섞이는 자리(전체·관심 공고)에서는 유형 이름을 줄 안에 같은 색으로 쓴다 —
    // 「최대 5억원」만으로는 융자인지 보조금인지 모른다(융자색은 어두워 검정처럼 읽힌다).
    return [{ k, text: only ? text : `${SUPPORT_VIEW_LABEL[k]} ${text}` }];
  });

  if (lines.length === 0) {
    /*
      **「아직 안 봤다」와 「봤는데 없더라」는 다른 사실이다.**

      예전엔 둘 다 「확인 중」이었다. 그런데 그중 대부분은 이미 해석이 끝난
      것이었고, 뽑을 게 없는 것이 정답이었다 — 실측 32건 중 23건이 「예비
      사회적기업 지정 계획」 「해양수산신기술(NET) 인증」 「화요IR 참여기업
      모집」처럼 **지원금도 프로그램도 없는** 인증·지정·행사 공고였다.

      「지원 내용 없음」은 실패 표시가 아니라 **독자를 아껴 주는 정보**다.
      돈 받으러 온 사람이 열어 볼 이유가 없다는 뜻이니까. 「확인 중」으로
      두면 열어 보고 나서야 알게 된다.

      유형 필터를 켜면 이런 공고는 애초에 목록에 안 들어온다 — 그 필터가
      해당 테이블에 행이 있는 공고만 내기 때문이다. 그래서 여기 남는 것은
      「전체 보기」일 때뿐이고, 그때는 숨기는 것보다 말해 주는 편이 낫다.
    */
    return (
      <div className="min-w-0 shrink-0 sm:w-52">
        <div className="truncate text-[13px] text-gray-300">
          {grant.analysis === 'pending' ? '확인 중' : '지원 내용 없음'}
        </div>
      </div>
    );
  }

  /*
    첫 열의 글자 규칙 — 모든 탭(전체·자금·바우처·융자·보증·시설·프로그램)에서 같다.
    유형 말머리(「보조금」「프로그램」)는 없다. 유형은 색이 말한다(SUPPORT_TONE).
    모든 줄 13px, 한 줄만 — 넘치면 말줄임(…).
  */
  return (
    <div className="min-w-0 shrink-0 sm:w-52">
      {lines.map(({ k, text }, i) => (
        <div
          key={k}
          className={cn('truncate text-[13px] font-semibold leading-snug tabular-nums', i > 0 && 'mt-0.5', SUPPORT_TONE[k])}
        >
          {text}
        </div>
      ))}
    </div>
  );
}

export function GrantCard({
  grant,
  now,
  support,
}: {
  grant: GrantListItemDto;
  now: Date;
  /** 켜져 있는 유형 필터. 있으면 카드도 그 유형만 말한다. */
  support?: GrantSupportFilter;
}) {
  const deadline = deadlineLabel(grant.applyEnd, now);
  const phase = grantPhase(grant, now);
  const regionText = regionLabel(grant);

  return (
    <Link
      to="/news/$id"
      params={{ id: grant.id }}
      // 유형 목록에서 눌렀으면 상세 상단 알약도 그 유형이 켜진다.
      search={support ? { tab: support } : {}}
      className="group block border-b border-gray-100 py-5 transition-colors last:border-b-0 hover:bg-gray-50/60 focus-visible:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
    >
      <div className="flex flex-col gap-2 px-1 sm:flex-row sm:items-baseline sm:gap-6">
        {/* 모바일: 셋째 열이 없다. 진행 단계 알약이 말머리처럼 맨 위에 선다. */}
        <span
          className={cn(
            'self-start rounded-full px-2.5 py-1 text-xs font-medium tabular-nums sm:hidden',
            PHASE_CLASS[phase.key],
          )}
        >
          {phase.key === 'closing' || phase.key === 'open' ? deadline.text : phase.label}
        </span>
        <Support grant={grant} only={support} />

        <div className="min-w-0 flex-1">
          <h3
            className={cn(
              'text-base font-medium leading-snug group-hover:text-gray-900 group-hover:underline underline-offset-2',
              pastTitleClass(phase.key),
            )}
          >
            {grant.title}
          </h3>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
            {regionText ? <span className="font-medium text-gray-600">{regionText}</span> : null}
            {grant.supportField ? (
              <>
                <span aria-hidden className="text-gray-300">·</span>
                <span>{grant.supportField}</span>
              </>
            ) : null}
            {grant.institution ? (
              <>
                <span aria-hidden className="text-gray-300">·</span>
                <span className="truncate">{grant.institution}</span>
              </>
            ) : null}
            {/*
              출처는 공고의 속성이 아니라 **어디서 가져왔는가**다. 같은 줄에
              점으로 잇지 않고 테두리로 구분한다 — 지역·분야·기관과 종류가
              다른 정보라 같은 모양으로 두면 하나로 읽힌다.
            */}
            <span className="shrink-0 rounded border border-gray-200 px-1.5 py-px text-[11px] text-gray-400">
              {shortSourceName(grant.sourceName)}
            </span>
            <AttachmentPill count={grant.attachmentCount} />
          </div>
        </div>

        {/*
          진행 단계와 남은 일수를 함께 보여준다. 「D-4」만으로는 결과 공고와
          모집 공고가 구별되지 않고, 「모집중」만으로는 급한지 알 수 없다.
        */}
        <span
          className={cn(
            'hidden shrink-0 rounded-full px-2.5 py-1 text-xs font-medium tabular-nums sm:inline-flex sm:self-baseline',
            PHASE_CLASS[phase.key],
          )}
        >
          {phase.key === 'closing' || phase.key === 'open' ? deadline.text : phase.label}
        </span>
      </div>
    </Link>
  );
}
